/**
 * Shared TOTP enrollment widget: fetch QR → verify a code → show one-time backup codes.
 * Used both during forced enrollment (login/registration, with an mfaToken) and from
 * Settings (no token — uses the logged-in session). Calls onComplete(session) after the
 * user acknowledges their backup codes; the parent decides how to apply the session.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { ShieldCheck, Copy, Loader2, AlertTriangle, Check } from 'lucide-react';

export default function TwoFactorSetup({ authToken, onComplete, onCancel }) {
    const { setupMfa, enableMfa } = useAuth();
    const [phase, setPhase] = useState('loading'); // loading | scan | backup | error
    const [qr, setQr] = useState(null);
    const [secret, setSecret] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [backupCodes, setBackupCodes] = useState([]);
    const [session, setSession] = useState(null);
    const [savedAck, setSavedAck] = useState(false);

    const startSetup = useCallback(async () => {
        setPhase('loading');
        setError('');
        try {
            const data = await setupMfa(authToken);
            setQr(data.qrDataUrl);
            setSecret(data.secret);
            setPhase('scan');
        } catch (err) {
            setError(err.response?.data?.error || 'Could not start 2FA setup.');
            setPhase('error');
        }
    }, [setupMfa, authToken]);

    useEffect(() => { startSetup(); }, [startSetup]);

    const submitCode = async (e) => {
        e.preventDefault();
        if (code.length < 6) return;
        setSubmitting(true);
        setError('');
        const res = await enableMfa(code, authToken);
        setSubmitting(false);
        if (res.success) {
            setBackupCodes(res.backupCodes || []);
            setSession(res.session || null);
            setPhase('backup');
        } else {
            setError(res.error || 'Invalid code');
            setCode('');
        }
    };

    const copySecret = () => navigator.clipboard?.writeText(secret);
    const copyBackup = () => navigator.clipboard?.writeText(backupCodes.join('\n'));

    if (phase === 'loading') {
        return <div className="flex items-center justify-center py-12 text-text-muted"><Loader2 className="w-6 h-6 animate-spin" /></div>;
    }

    if (phase === 'error') {
        return (
            <div className="text-center py-8">
                <AlertTriangle className="w-8 h-8 text-accent-red mx-auto mb-3" />
                <p className="text-accent-red text-sm mb-4">{error}</p>
                <button onClick={startSetup} className="btn-secondary">Try again</button>
            </div>
        );
    }

    if (phase === 'backup') {
        return (
            <div>
                <div className="flex items-center gap-2 mb-2 text-accent-green">
                    <ShieldCheck className="w-5 h-5" />
                    <h3 className="font-semibold">Two-factor is on</h3>
                </div>
                <p className="text-sm text-text-secondary mb-4">
                    Save these backup codes somewhere safe. Each works once if you lose your authenticator. <strong>They won't be shown again.</strong>
                </p>
                <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-bg-tertiary rounded-lg p-4 mb-3">
                    {backupCodes.map((c) => <div key={c} className="text-center">{c}</div>)}
                </div>
                <button onClick={copyBackup} className="btn-secondary text-sm flex items-center gap-2 mb-4"><Copy className="w-4 h-4" /> Copy codes</button>
                <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
                    <input type="checkbox" checked={savedAck} onChange={(e) => setSavedAck(e.target.checked)} className="rounded w-4 h-4 accent-purple-500" />
                    I've saved my backup codes
                </label>
                <button
                    onClick={() => onComplete && onComplete(session)}
                    disabled={!savedAck}
                    className="w-full btn-primary py-3 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    <Check className="w-4 h-4" /> Continue
                </button>
            </div>
        );
    }

    // phase === 'scan'
    return (
        <div>
            <p className="text-sm text-text-secondary mb-4">
                Scan this QR code with an authenticator app (Google Authenticator, Authy, 1Password), then enter the 6-digit code it shows.
            </p>
            {qr && (
                <div className="flex justify-center mb-4">
                    <img src={qr} alt="2FA QR code" className="w-44 h-44 rounded-lg bg-white p-2" />
                </div>
            )}
            <div className="mb-4">
                <p className="text-xs text-text-muted mb-1">Can't scan? Enter this key manually:</p>
                <button onClick={copySecret} className="font-mono text-xs bg-bg-tertiary rounded px-3 py-2 w-full text-left flex items-center justify-between gap-2 hover:bg-bg-card transition-colors">
                    <span className="truncate">{secret}</span>
                    <Copy className="w-3 h-3 flex-none" />
                </button>
            </div>
            {error && <p className="text-accent-red text-sm mb-3">{error}</p>}
            <form onSubmit={submitCode}>
                <input
                    value={code}
                    onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                    placeholder="000000"
                    inputMode="numeric"
                    autoFocus
                    className="input text-center tracking-[0.5em] text-2xl font-mono mb-4"
                />
                <div className="flex gap-3">
                    {onCancel && <button type="button" onClick={onCancel} className="btn-secondary flex-1">Cancel</button>}
                    <button type="submit" disabled={code.length < 6 || submitting} className="btn-primary flex-1 py-3 disabled:opacity-50 flex items-center justify-center gap-2">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                        Verify & enable
                    </button>
                </div>
            </form>
        </div>
    );
}
